import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Descriptions,
  Tag,
  Spin,
  message,
  Button,
  Modal,
  Form,
  Input,
  Divider,
  List,
  Space,
  Tooltip,
  Alert,
} from "antd";
import {
  UserOutlined,
  EditOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  IdcardOutlined,
  HomeOutlined,
  CrownOutlined,
  TeamOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  KeyOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";

const { Title, Text } = Typography;

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

interface OrganizationInfo {
  id: string;
  name: string;
  role: string;
  subscription_status: string;
  is_free_trial: boolean;
  free_trial_expires_at?: string;
  billing_cycle?: string;
  facilities?: FacilityInfo[];
}

interface FacilityInfo {
  id: string;
  name: string;
  role: string;
  organization_id: string;
  organization_name: string;
  street1?: string;
  city?: string;
  state?: string;
}

interface ProfileData {
  id: string;
  name: string;
  email: string;
  created_at: string;
  organizations: OrganizationInfo[];
  user_trial_expires_at?: string;
  trial_days_remaining?: number;
}

export const Profile: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch profile");
      }

      const data = await response.json();
      setProfileData(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      message.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleEditProfile = () => {
    form.setFieldsValue({
      name: profileData?.name,
    });
    setEditModalVisible(true);
  };

  const handleSaveProfile = async (values: any) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: values.name }),
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      const updatedData = await response.json();
      setProfileData(updatedData);
      setEditModalVisible(false);
      message.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Failed to update profile");
    }
  };

  const formatTrialExpiry = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return "Expired";
    } else if (diffDays === 0) {
      return "Expires today";
    } else if (diffDays === 1) {
      return "Expires tomorrow";
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "50vh" 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <Alert
        message="Profile Not Found"
        description="Unable to load your profile information."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <Title level={2}>
        <UserOutlined /> Profile
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card
            title="Personal Information"
            extra={
              <Button 
                type="primary" 
                icon={<EditOutlined />} 
                onClick={handleEditProfile}
              >
                Edit Profile
              </Button>
            }
          >
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Name">
                <Space>
                  <UserOutlined />
                  {profileData.name}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <Space>
                  <MailOutlined />
                  {profileData.email}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {/* Organizations */}
          <Card 
            title="Organizations & Facilities" 
            extra="Need to join an organization? Ask your admin for an invitation."
            style={{ marginTop: "24px" }}
          >
            {profileData.organizations.length === 0 ? (
              <Alert
                message="No Organizations"
                description="You are not currently a member of any organizations. Create a new organization or ask to be invited to an existing one."
                type="info"
                showIcon
              />
            ) : (
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {profileData.organizations.map((org) => (
                  <Card 
                    key={org.id} 
                    type="inner"
                    title={
                      <Space>
                        <TeamOutlined />
                        {org.name}
                        <Tag 
                          color={org.role === 'Owner' ? 'gold' : 'blue'}
                          icon={org.role === 'Owner' ? <CrownOutlined /> : <UserOutlined />}
                        >
                          {org.role}
                        </Tag>
                      </Space>
                    }
                    extra={
                      org.subscription_status && (
                        <Tag 
                          color={org.subscription_status === 'Active' ? 'green' : 'orange'}
                          icon={<CheckCircleOutlined />}
                        >
                          {org.subscription_status}
                        </Tag>
                      )
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Subscription">
                            {org.subscription_status ? (
                              <Space>
                                <Tag 
                                  color={org.subscription_status === 'Active' ? 'green' : 'orange'}
                                >
                                  {org.subscription_status}
                                </Tag>
                                {org.billing_cycle && (
                                  <Text type="secondary">
                                    ({org.billing_cycle} billing)
                                  </Text>
                                )}
                              </Space>
                            ) : (
                              <Text type="secondary">Not available</Text>
                            )}
                          </Descriptions.Item>
                          {org.subscription_status !== 'Active' && org.free_trial_expires_at && (
                            <Descriptions.Item label="Trial Expires">
                              <Text>
                                {formatTrialExpiry(org.free_trial_expires_at)}
                              </Text>
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Col>
                      <Col xs={24} md={12}>
                        <div>
                          <Text strong>Facilities ({(org.facilities || []).length})</Text>
                          <div style={{ marginTop: "8px" }}>
                            {(!org.facilities || org.facilities.length === 0) ? (
                              <Text type="secondary">No facilities</Text>
                            ) : (
                              <Space direction="vertical" size="small">
                                {org.facilities.map((facility) => (
                                  <div key={facility.id}>
                                    <Space>
                                      <HomeOutlined />
                                      <Text strong>{facility.name}</Text>
                                    </Space>
                                    {(facility.street1 || facility.city) && (
                                      <div style={{ marginLeft: "20px" }}>
                                        <Text type="secondary" style={{ fontSize: "12px" }}>
                                          <EnvironmentOutlined /> {[
                                            facility.street1,
                                            facility.city,
                                            facility.state
                                          ].filter(Boolean).join(', ')}
                                        </Text>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </Space>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      

      {/* Edit Profile Modal */}
      <Modal
        title="Edit Profile"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: "Please enter your name" },
              { min: 2, message: "Name must be at least 2 characters" },
            ]}
          >
            <Input 
              placeholder="Enter your full name"
              prefix={<UserOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}; 